import { useTranslation } from "react-i18next";
import { Form } from "semantic-ui-react";

import { DATE_FORMATS_FNS, UploadFilesZone, UploadedFilesPreview } from "components";
import ENV from "config/Env";
import { usePermanentRealTimeUser, useUploadAttachment } from "hooks";
import { APPOINTMENT_NOTE_TYPES } from "models";
import { useAppointmentLocation } from "modules/AppointmentDetails/hooks";
import { getFilesWithinSizeLimit } from "utils/common";
import { formatCurrentUTCDate, formatDate } from "utils/dateHelperFunctions";

import type { PreviewAttachment } from "components";
import type { Appointment, AppointmentNote, AppointmentNoteAttachment } from "models";
import type { Dispatch, SetStateAction } from "react";

type AppointmentNoteAttachmentsProps = {
  hasValidationErrors: boolean;
  noteType: APPOINTMENT_NOTE_TYPES;
  appointment: Appointment;
  appointmentNote: AppointmentNote | null;
  attachments: AppointmentNoteAttachment[] | null;
  setAttachments: Dispatch<SetStateAction<AppointmentNoteAttachment[] | null>>;
  onUpload?: (isUploading: boolean) => void;
};

//TODO: This is also a candidate for upload attachment to have coming from one component, can't do it now in this ticket
// Didn't changed the logic, as it will be part of the ticket where we cleanup this upload feature.
export const AppointmentNoteAttachments = ({
  hasValidationErrors,
  noteType,
  appointment,
  appointmentNote,
  attachments,
  setAttachments,
  onUpload
}: AppointmentNoteAttachmentsProps) => {
  const { t } = useTranslation();
  const { user } = usePermanentRealTimeUser();
  const selectedLocation = useAppointmentLocation();
  const { handleUploadFileAttachment, isUploadingAttachment } = useUploadAttachment("/notes/attachment/upload", ENV.appointmentBaseURL, onUpload);

  const uploadedAttachmentsPreview: PreviewAttachment[] =
    attachments?.map(({ url, name, created_on, user_id, username }) => ({ url, name, created_on, user_id, username })) || [];

  const handleUploadAttachment = async (file: File) => {
    const extension = file.name.lastIndexOf(".") > -1 ? file.name.slice(file.name.lastIndexOf(".") + 1) : "unknown";
    const uploadFile = new File([file], file.name, { type: file.type || extension });
    const formData = new FormData();

    const date = formatDate(appointment.created_on, DATE_FORMATS_FNS.yearMonthDateSlash);
    const path = `${selectedLocation?.id}_${selectedLocation?.name}/${date}/${appointment.id}_${appointment.wo_nr}`;

    formData.append("file", uploadFile);
    formData.append("path", path);

    const attachmentUrl = await handleUploadFileAttachment({ formData });

    if (attachmentUrl) {
      const newFile: AppointmentNoteAttachment = {
        id: -1,
        user_id: user?.id || -1,
        appointment_note_id: -1,
        url: attachmentUrl,
        type: file.type || extension,
        name: file.name,
        created_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
        updated_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
        username: `${user?.first_name || ""} ${user?.last_name || ""}`,
        external_source: ""
      };

      if (noteType === APPOINTMENT_NOTE_TYPES.Bill) setAttachments([newFile]);
      else setAttachments(attach => (attach ? [...attach, newFile] : [newFile]));
    }
  };

  const handleDeleteAttachment = (url: string) => {
    if (attachments?.length) setAttachments(attach => (attach ? attach.filter(attachment => attachment.url !== url) : null));
  };

  const handleFileChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    getFilesWithinSizeLimit([...(evt.target.files ?? [])], t).forEach(file => handleUploadAttachment(file));
  };

  const handleFileDragOver = (evt: React.DragEvent<HTMLInputElement>) => evt.preventDefault();

  const handleFileDrop = (evt: React.DragEvent<HTMLInputElement>) => {
    evt.preventDefault();
    getFilesWithinSizeLimit([...evt.dataTransfer.files], t).forEach(file => handleUploadAttachment(file));
  };

  const isBillAppointmentWithAttachments = noteType === APPOINTMENT_NOTE_TYPES.Bill && !!attachments?.length;

  return (
    <>
      {!isBillAppointmentWithAttachments && (
        <Form.Field
          error={hasValidationErrors && !attachments?.length && [APPOINTMENT_NOTE_TYPES.Attachment, APPOINTMENT_NOTE_TYPES.Bill].includes(Number(noteType))}
          required={[APPOINTMENT_NOTE_TYPES.Attachment, APPOINTMENT_NOTE_TYPES.Bill].includes(Number(noteType))}
        >
          <UploadFilesZone
            hasError={hasValidationErrors && !attachments?.length && [APPOINTMENT_NOTE_TYPES.Attachment, APPOINTMENT_NOTE_TYPES.Bill].includes(Number(noteType))}
            display={![APPOINTMENT_NOTE_TYPES.ParkingLocation, APPOINTMENT_NOTE_TYPES.TempDriver].includes(Number(noteType))}
            isUploading={isUploadingAttachment}
            onFileInputChange={handleFileChange}
            onFileDragOver={handleFileDragOver}
            onFileDrop={handleFileDrop}
          />
        </Form.Field>
      )}
      <UploadedFilesPreview attachments={uploadedAttachmentsPreview} onDeleteAttachment={handleDeleteAttachment} fromAPI={Boolean(appointmentNote?.public_account_id)} />
    </>
  );
};
